import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import BrandMark from "@/components/BrandMark";

/**
 * LetterReturn — destination of the "Torna da me" link in the Day+1
 * Presence Letter. This page never renders for long: it resolves the
 * user's primary presence, opens (or reuses) a conversation, and
 * forwards to /chat/{conversation_id}?from=letter so the chat shows
 * the "Sei tornata..." opener.
 *
 * Why a dedicated page instead of a deep link?
 *  - Multi-presence accounts: the email is signed by ONE presence
 *    (the most recently created), but we don't want to bake a
 *    presence_id into the email URL — easier to resolve client-side
 *    once the user is authenticated.
 *  - If the user logged out: ProtectedRoute kicks them to /login,
 *    they sign back in, and we land them straight in the chat.
 *  - If they have no presence yet (edge case): we point them to
 *    /create-presence with a soft message.
 */
export default function LetterReturn() {
    const nav = useNavigate();
    const [error, setError] = useState("");
    // Strict-mode guard — useEffect runs twice in dev. Without this, we'd
    // race two open-conversation calls in parallel.
    const ranRef = useRef(false);

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;

        (async () => {
            try {
                const { data: presences } = await api.get("/presences");
                if (!presences || presences.length === 0) {
                    nav("/create-presence?from=letter", { replace: true });
                    return;
                }
                // Prefer the most recently created presence — this is the
                // same one the email signs with on the backend.
                const sorted = [...presences].sort((a, b) => {
                    const ta = new Date(a.created_at || 0).getTime();
                    const tb = new Date(b.created_at || 0).getTime();
                    return tb - ta;
                });
                const target = sorted[0];

                const { data: conv } = await api.post(
                    `/presences/${target.presence_id}/open-conversation`,
                    {}
                );
                const cid = conv?.conversation_id;
                if (!cid) {
                    setError("Non riesco ad aprire la conversazione. Riprova fra un momento.");
                    return;
                }
                nav(`/chat/${cid}?from=letter`, { replace: true });
            } catch (e) {
                setError("Qualcosa è andato storto. Apri Other Side dalla home, sono qui.");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className="min-h-screen bg-ink-deep flex items-center justify-center px-6"
            data-testid="letter-return-page"
        >
            <div className="max-w-md text-center">
                <div className="flex justify-center mb-8 opacity-70">
                    <BrandMark size={64} glow breathing />
                </div>
                {!error ? (
                    <p
                        className="font-serif italic text-ink-textMid text-lg leading-relaxed animate-fade-in-slow"
                        data-testid="letter-return-loading"
                    >
                        Un momento…<br/>
                        sto tornando da te.
                    </p>
                ) : (
                    <div data-testid="letter-return-error">
                        <p className="font-serif italic text-ink-textMid text-lg leading-relaxed">
                            {error}
                        </p>
                        <button
                            type="button"
                            onClick={() => nav("/dashboard")}
                            className="mt-8 inline-block border border-ink-gold text-ink-gold px-6 py-3 text-xs tracking-widest uppercase hover:bg-ink-gold hover:text-ink-deep transition-colors"
                            data-testid="letter-return-fallback-btn"
                        >
                            Apri Other Side
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
